import axios from "axios"

// API Configuration
// Automatically use localhost server when running on localhost
const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
const API_BASE_URL = isLocalhost
  ? "http://localhost:5001/api"
  : (process.env.REACT_APP_API_URL || "https://server.turbo.sd/api")

console.log("🔌 API Base URL:", API_BASE_URL)
console.log("🌍 Environment:", {
  hostname: window.location.hostname,
  isLocalhost,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  NODE_ENV: process.env.NODE_ENV
})

// API Client
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Add auth token to requests
apiClient.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("admin_token")
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response

      switch (status) {
        case 401:
          // Unauthorized - trigger session expiry dialogue
          if (!window.location.pathname.includes('/login')) {
            window.dispatchEvent(new CustomEvent('session-expired', { detail: { type: 'admin' } }))
          }
          break
        case 403:
          // Forbidden
          console.error("Access forbidden:", data?.message || "Insufficient permissions")
          if (data?.message === "Invalid or expired token" || data?.message === "Access token required" || data?.message === "Invalid token or user inactive") {
            if (!window.location.pathname.includes('/login')) {
              window.dispatchEvent(new CustomEvent('session-expired', { detail: { type: 'admin' } }))
            }
          }
          break
        case 404:
          // Not found
          console.error("Resource not found:", data?.message || "Requested resource not found")
          break
        case 422:
          // Validation error
          console.error("Validation error:", data?.message || "Invalid data provided")
          break
        case 500:
          // Server error
          console.error("Server error:", data?.message || "Internal server error")
          break
        default:
          console.error("API error:", data?.message || `Request failed with status ${status}`)
      }

      // Enhance error object with user-friendly message
      error.userMessage = data?.message || getDefaultErrorMessage(status)
    } else if (error.request) {
      // Network error
      console.error("Network error:", error.message)
      error.userMessage = "Network connection failed. Please check your internet connection and try again."
    } else {
      // Other error
      console.error("Request error:", error.message)
      error.userMessage = "An unexpected error occurred. Please try again."
    }

    return Promise.reject(error)
  }
)

// Helper function for default error messages
function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400: return "Invalid request. Please check your input and try again."
    case 401: return "Session expired. Please log in again."
    case 403: return "You don't have permission to perform this action."
    case 404: return "The requested resource was not found."
    case 422: return "Please check your input and try again."
    case 429: return "Too many requests. Please wait a moment and try again."
    case 500: return "Server error. Please try again later."
    case 503: return "Service temporarily unavailable. Please try again later."
    default: return "An error occurred. Please try again."
  }
}

// Auth API
export const authAPI = {
  login: (credentials: { email: string; password: string }) => apiClient.post("/admin/login", credentials),

  adminLogin: (email: string, password: string) => apiClient.post("/admin/login", { email, password }),

  logout: () => apiClient.post("/admin/logout"),

  getProfile: () => {
    console.log("API: Getting profile from", API_BASE_URL + "/admin/profile")
    return apiClient.get("/admin/profile")
  },

  updateProfile: (data: { username?: string; fullName?: string; email?: string; phone?: string; bio?: string; profileImage?: string }) => {
    console.log("API: Updating profile with data:", data)
    return apiClient.put("/admin/profile", data)
  },

  updateProfileImage: (imageUrl: string) => {
    console.log("API: Updating profile image, length:", imageUrl?.length)
    return apiClient.post("/admin/profile/image", { imageUrl })
  },

  changePassword: (data: { currentPassword: string; newPassword: string }) => {
    console.log("API: Changing password")
    return apiClient.put("/admin/profile/password", data)
  },
}



// Dashboard API
export const dashboardAPI = {
  getStats: () => apiClient.get("/admin/dashboard/stats"),

  getRecentOrders: () => apiClient.get("/admin/dashboard/recent-orders"),

  getAnalytics: (params?: any) => apiClient.get("/admin/dashboard/analytics", { params }),
}

// Restaurant API - Updated to use admin routes
export const restaurantAPI = {
  getRestaurants: (params?: any) => apiClient.get("/admin/restaurants", { params }),

  getRestaurant: (id: string) => apiClient.get(`/admin/restaurants/${id}`),

  createRestaurant: (data: any) => apiClient.post("/admin/restaurants", data),

  updateRestaurant: (id: string, data: any) => apiClient.put(`/admin/restaurants/${id}`, data),

  deleteRestaurant: (id: string) => apiClient.delete(`/admin/restaurants/${id}`),
}

// Public Restaurant API (no authentication required)
export const publicRestaurantAPI = {
  getRestaurants: (params?: any) => apiClient.get("/restaurants", { params }),

  getRestaurant: (id: string) => apiClient.get(`/restaurants/${id}`),
}

// Menu Book API (public, no authentication required)
export const menuBookAPI = {
  getRestaurants: (params?: any) =>
    apiClient.get("/menu-book/restaurants", {
      params,
      headers: { "x-menu-book": "true" },
    }),

  getRestaurantBySlug: (slug: string) =>
    apiClient.get(`/menu-book/restaurants/${slug}`, {
      headers: { "x-menu-book": "true" },
    }),

  trackRestaurantView: (slug: string) =>
    apiClient.post(`/menu-book/restaurants/${slug}/view`, {}, {
      headers: { "x-menu-book": "true" },
    }).catch(() => {}), // Silently fail — analytics should never block UX

  trackItemView: (itemId: string) =>
    apiClient.post(`/menu-book/items/${itemId}/view`, {}, {
      headers: { "x-menu-book": "true" },
    }).catch(() => {}),

  getAnalytics: (restaurantId: string, days?: number) =>
    apiClient.get(`/menu-book/analytics/${restaurantId}`, {
      params: { days },
      headers: { "x-menu-book": "true" },
    }),
}



// Captain API - Updated to use admin routes
export const captainAPI = {
  getCaptains: (params?: any) => apiClient.get("/admin/captains", { params }),

  getCaptain: (id: string) => apiClient.get(`/admin/captains/${id}`),

  createCaptain: (data: any) => apiClient.post("/admin/captains", data),

  updateCaptain: (id: string, data: any) => apiClient.put(`/admin/captains/${id}`, data),

  deleteCaptain: (id: string) => apiClient.delete(`/admin/captains/${id}`),

  approveCaptain: (id: string) => apiClient.post(`/admin/captains/${id}/approve`),

  rejectCaptain: (id: string) => apiClient.post(`/admin/captains/${id}/reject`),
}

// User API - Updated to use admin routes
export const userAPI = {
  getUsers: async (params?: any) => {
    try {
      console.log("🔌 API: Calling getUsers with params:", params);
      const response = await apiClient.get("/admin/users", { params });
      console.log("📡 API: Raw response:", response);
      console.log("📋 API: Response data:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ API: getUsers error:", error);
      throw error;
    }
  },

  getUser: (id: string) => apiClient.get(`/admin/users/${id}`),

  updateUser: (id: string, data: any) => apiClient.put(`/admin/users/${id}`, data),

  deleteUser: (id: string) => apiClient.delete(`/admin/users/${id}`),

  toggleStatus: (id: string) => apiClient.put(`/admin/users/${id}/toggle-status`),
}

// Restaurant Owner API - Admin routes
export const restaurantOwnerAPI = {
  getRestaurantOwners: async (params?: any) => {
    try {
      console.log("🔌 API: Calling getRestaurantOwners with params:", params);
      const response = await apiClient.get("/admin/restaurant-owners", { params });
      console.log("📡 API: Raw response:", response);
      console.log("📋 API: Response data:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ API: getRestaurantOwners error:", error);
      throw error;
    }
  },

  getRestaurantOwner: (id: string) => apiClient.get(`/admin/restaurant-owners/${id}`),

  approveRestaurantOwner: (id: string) => apiClient.post(`/admin/restaurant-owners/${id}/approve`),

  rejectRestaurantOwner: (id: string) => apiClient.post(`/admin/restaurant-owners/${id}/reject`),

  toggleRestaurantOwnerStatus: (id: string) => apiClient.put(`/admin/restaurant-owners/${id}/toggle-status`),

  deleteRestaurantOwner: (id: string) => apiClient.delete(`/admin/restaurant-owners/${id}`),
}

// Order API - Updated to use admin routes
export const orderAPI = {
  getOrders: (params?: any) => apiClient.get("/admin/orders", { params }),

  getOrder: (id: string) => apiClient.get(`/admin/orders/${id}`),

  updateOrderStatus: (id: string, status: string) => apiClient.put(`/admin/orders/${id}/status`, { status }),

  assignCaptain: (orderId: string, captainId: string) =>
    apiClient.post(`/admin/orders/${orderId}/assign`, { captainId }),
}

// For backward compatibility - alias for orderAPI
export const ordersAPI = orderAPI
export const usersAPI = userAPI

// Notification API - Updated to use correct routes
export const notificationAPI = {
  sendBroadcast: (data: {
    title: string
    body: string
    targetAudience: string
    customData?: any
  }) => apiClient.post("/notifications/broadcast", data),

  sendCustom: (data: {
    title: string
    body: string
    userIds: string[]
    customData?: any
  }) => apiClient.post("/notifications/custom", data),

  getNotificationHistory: (params?: any) => apiClient.get("/notifications/history", { params }),

  getNotificationStats: () => apiClient.get("/notifications/stats"),
}

// Analytics API
export const analyticsAPI = {
  getRevenue: (params?: any) => apiClient.get("/admin/analytics/revenue", { params }),

  getOrders: (params?: any) => apiClient.get("/admin/analytics/orders", { params }),

  getUsers: (params?: any) => apiClient.get("/admin/analytics/users", { params }),

  getCaptains: (params?: any) => apiClient.get("/admin/analytics/captains", { params }),

  getRestaurants: (params?: any) => apiClient.get("/admin/analytics/restaurants", { params }),

  getOverview: (period?: any) => apiClient.get("/admin/analytics/overview", { params: { period } }),

  getOrdersAnalytics: (period?: any) => apiClient.get("/admin/analytics/orders-detail", { params: { period } }),

  getRevenueAnalytics: (period?: any) => apiClient.get("/admin/analytics/revenue-detail", { params: { period } }),

  getTopRestaurants: (period?: any) => apiClient.get("/admin/analytics/top-restaurants", { params: { period } }),
}

// Food Items API - Admin routes
export const foodItemsAPI = {
  getFoodItems: (params?: any) => apiClient.get("/admin/food-items", { params }),

  getFoodItem: (id: string) => apiClient.get(`/admin/food-items/${id}`),

  createFoodItem: (data: any) => apiClient.post("/admin/food-items", data),

  updateFoodItem: (id: string, data: any) => apiClient.put(`/admin/food-items/${id}`, data),

  deleteFoodItem: (id: string) => apiClient.delete(`/admin/food-items/${id}`),

  toggleAvailability: (id: string) => apiClient.put(`/admin/food-items/${id}/toggle-availability`),

  bulkUpdate: (data: { foodItemIds: string[], updates: any }) =>
    apiClient.post("/admin/food-items/bulk-update", data),

  getStats: () => apiClient.get("/admin/food-items/stats/overview"),

  // Get food items by restaurant (public route)
  getFoodItemsByRestaurant: (restaurantId: string, params?: any) =>
    apiClient.get(`/food-items/restaurant/${restaurantId}`, { params }),
};

// Promotions API - Admin routes
export const promotionsAPI = {
  getPromotions: (params?: any) => apiClient.get("/promos", { params }),

  getPromotion: (id: string) => apiClient.get(`/promos/${id}`),

  createPromotion: (data: any) => apiClient.post("/promos", data),

  updatePromotion: (id: string, data: any) => apiClient.put(`/promos/${id}`, data),

  deletePromotion: (id: string) => apiClient.delete(`/promos/${id}`),

  getPromotionStats: () => apiClient.get("/promos/stats"),

  // Public routes for validation
  validatePromoCode: (data: { code: string; orderAmount?: number; deliveryFee?: number }) =>
    apiClient.post("/promos/validate", data),

  getActivePromotions: () => apiClient.get("/promos/active"),
};

// Feedback API - Admin routes
export const feedbackAPI = {
  getAllFeedback: (params?: any) => apiClient.get("/feedback/admin/all", { params }),

  getFeedback: (id: string) => apiClient.get(`/feedback/${id}`),

  respondToFeedback: (id: string, data: { message: string }) =>
    apiClient.post(`/feedback/${id}/respond`, data),

  updateFeedbackStatus: (id: string, status: string) =>
    apiClient.patch(`/feedback/${id}/status`, { status }),

  getFeedbackStats: () => apiClient.get("/feedback/admin/stats"),

  // Public routes
  createFeedback: (data: any) => apiClient.post("/feedback", data),

  getRestaurantFeedback: (restaurantId: string, params?: any) =>
    apiClient.get(`/feedback/restaurant/${restaurantId}`, { params }),

  getCaptainFeedback: (captainId: string, params?: any) =>
    apiClient.get(`/feedback/captain/${captainId}`, { params }),

  getUserFeedback: (params?: any) => apiClient.get("/feedback/my-reviews", { params }),

  voteHelpful: (id: string) => apiClient.post(`/feedback/${id}/helpful`),

  reportFeedback: (id: string, data: { reason: string }) =>
    apiClient.post(`/feedback/${id}/report`, data),
};

// Customer API - Admin routes
export const customerAPI = {
  getCustomers: (params?: any) => apiClient.get("/customers", { params }),

  searchByPhone: (phone: string) => apiClient.get(`/customers/search-by-phone/${encodeURIComponent(phone)}`),

  getCustomer: (id: string) => apiClient.get(`/customers/${id}`),

  createCustomer: (data: any) => apiClient.post("/customers", data),

  updateCustomer: (id: string, data: any) => apiClient.put(`/customers/${id}`, data),

  addAddress: (customerId: string, data: any) =>
    apiClient.post(`/customers/${customerId}/addresses`, data),

  updateAddress: (customerId: string, addressId: string, data: any) =>
    apiClient.put(`/customers/${customerId}/addresses/${addressId}`, data),

  deleteAddress: (customerId: string, addressId: string) =>
    apiClient.delete(`/customers/${customerId}/addresses/${addressId}`),

  setDefaultAddress: (customerId: string, addressId: string) =>
    apiClient.patch(`/customers/${customerId}/addresses/${addressId}/default`),

  getCustomerOrders: (customerId: string, params?: any) =>
    apiClient.get(`/customers/${customerId}/orders`, { params }),

  toggleFavoriteRestaurant: (customerId: string, restaurantId: string) =>
    apiClient.post(`/customers/${customerId}/favorites/restaurants/${restaurantId}`),

  getCustomerStats: () => apiClient.get("/customers/stats/summary"),
};

// Delivery Orders API - Admin routes
export const deliveryOrdersAPI = {
  getDeliveryOrders: (params?: any) => apiClient.get("/admin/delivery-orders", { params }),

  getActiveLocations: () => apiClient.get("/admin/delivery-orders/active-locations"),

  getAvailableCaptains: (params?: any) => apiClient.get("/admin/available-captains", { params }),

  createPhoneOrder: (data: any) => apiClient.post("/admin/phone-orders", data),

  updateOrderStatus: (orderId: string, status: string) =>
    apiClient.put(`/admin/orders/${orderId}/status`, { status }),

  assignCaptain: (orderId: string, captainId: string) =>
    apiClient.post(`/admin/orders/${orderId}/assign`, { captainId }),
};

// Settings API - Admin routes
export const settingsAPI = {
  getSettings: () => apiClient.get("/admin/settings"),

  updateSettings: (data: any) => apiClient.put("/admin/settings", data),

  calculateDeliveryFee: (distanceKm: number, orderSubtotal?: number) =>
    apiClient.post("/admin/settings/calculate-delivery-fee", { distanceKm, orderSubtotal }),
};

export default apiClient
